import { useMemo } from 'react';

import { SkeletonRect, Text } from '@ekhodealer/ekho-common/rev';

import { useGetInventoryItem } from '../api/useGetInventoryItem';
import { getPublishBlockers } from '../lib/utils';

interface PublishBlockersProps {
	inventoryItemId: string;
}

/**
 * Component that displays a list of publish blockers for an inventory item.
 * Checks for:
 * - At least one image required
 * - Failed detail website scrape (when externalWebsiteId exists)
 * - Assignment status that prevents publishing
 */
export const PublishBlockers = ({ inventoryItemId }: PublishBlockersProps) => {
	const { data: inventoryItem, isPending } = useGetInventoryItem(inventoryItemId);

	const blockers = useMemo(() => getPublishBlockers(inventoryItem), [inventoryItem]);

	// Show loading skeletons while data is loading
	if (isPending) {
		return (
			<div>
				<div style={{ color: '#ffffff', marginBottom: '8px' }}>
					<Text size='bodySmall' color='tertiary'>Blocked from publishing:</Text>
				</div>
				<ul style={{ margin: 2, paddingLeft: '20px', listStyle: 'none' }}>
					<li>
						<div style={{ filter: 'brightness(3) opacity(0.6)' }}>
							<SkeletonRect width='150px' height='12px' />
						</div>
					</li>
					<li>
						<div style={{ filter: 'brightness(3) opacity(0.6)' }}>
							<SkeletonRect width='100px' height='12px' />
						</div>
					</li>
				</ul>
			</div>
		);
	}

	// Don't render anything if there are no blockers
	if (blockers.length === 0) {
		return null;
	}

	return (
		<div>
			<div style={{ color: '#ffffff', marginBottom: '8px' }}>
				<Text size='bodySmall' color='tertiary'>Blocked from publishing:</Text>
			</div>
			<ul style={{ margin: 2, paddingLeft: '20px' }}>
				{blockers.map((blocker, index) => (
					<li key={index}>
						<Text size='bodySmall' color='tertiary'>{blocker}</Text>
					</li>
				))}
			</ul>
		</div>
	);
};
